<template>
  <div>
    <!-- 支払処理結果一覧 -->
    <BaseTableList
        :class="'payment-set-list' + ($isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.DETAIL) ? ' row-clickable-table' : '')"
        :table-set-list="paymentSetList"
        :cell-class-name="cellClassName"
        :display-items="rejectedPaymentSets"
        @row-click="onRowClick">
      <!-- オペレーションボタン -->
      <template
          slot="operation"
          slot-scope="{ row }">
        <BaseOperationTooltip
            v-if="showTooltip"
            :list="getTooltipList(row.row)"
            placement="bottom-end" />
      </template>
    </BaseTableList>

    <!-- 支払処理伝票ダイアログ -->
    <SlipDialog
        type="paymentSet"
        :visible.sync="detailDialogVisible"
        :detail-data="detailData" />

    <!-- 返金日登録ダイアログ -->
    <BaseDialog
        title="返金日登録"
        :visible.sync="regRefundDateVisible"
        :show-only-close-button="false"
        class="regRefundDateDialog"
        width="30%"
        :disable-ok-button="regRefundDateDisableOK"
        @closed="regRefundDateVisible = false"
        @cancel="regRefundDateVisible = false"
        @ok="onRegRefundDateOk">
      <template slot="contents">
        <div class="dialog-content">
          <BaseTableDetail
              :validate-scope="regRefundDateValidateScope"
              :editable="regRefundDateEditable"
              :table-set-list="regRefundDateTableSetList"
              :display-item="regRefundDateData" />
        </div>
      </template>
    </BaseDialog>
  </div>
</template>

<style scoped>
.payment-set-list >>> td.payment-set-status-error {
  color: #ff5a5a;
}
.dialog-content {
  margin: 0 20px;
}

.regRefundDateDialog .dialog-content {
  padding: 10px;
  display: flex;
  color: #9da6ba;
  font-weight: bold;
}

.regRefundDateDialog .dialog-content span {
  width: 150px;
  display: flex;
  align-items: center;
}

.regRefundDateDialog .dialog-content div,
.regRefundDateDialog .dialog-content  >>> .el-date-editor.el-input {
  width: 100%;
}

</style>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import x from '@/components/mixins/mixin'
import tableRowAnimator from '@/components/mixins/tableRowAnimator'
import * as define from '@/define'
import * as config from '@/config'
import common from '@/utils/common'
import message from '@/utils/message'

import BaseOperationTooltip from '@/components/molecules/BaseOperationTooltip'
import BaseTableList from '@/components/molecules/BaseTableList'
import BaseDialog from '@/components/molecules/BaseDialog'
import BaseTableDetail from '@/components/molecules/BaseTableDetail'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'

import SlipDialog from '@/components/pages/order/SlipDialog'

const EDITABLE = Object.freeze({
  [define.ORDER_MODE.NORMAL]: Object.freeze({ paymentMethod: true, editableCondition: true })
})

const ERROR_REASON = {
  unprocessed: '処理待ち',
  processing: '処理中',
  processed: '処理済み',
  checking: '確認中',
  canceled: 'キャンセル済み',
  void: '無効なステータス',
  manualCompletion: '手動完了',
  errorEnd: 'エラー終了',
  errorProcessed: 'エラー処理済み'
}

export default {
  name: 'OrderDetailPaymentSet',
  components: {
    BaseOperationTooltip,
    BaseTableList,
    SlipDialog,
    BaseDialog,
    BaseTableDetail
  },
  mixins: [x, tableRowAnimator],
  inject: ['parentValidator'],
  props: {
    mode: {
      type: Number,
      default: define.ORDER_MODE.NORMAL
    },
    paymentSets: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      detailDialogVisible: false,
      detailData: {},
      isShownDialog: false,
      selectValue: null,
      movement: {},
      dialog: {},
      payment: [],
      changePayments: {
        srcPaymentSet: {},
        destPaymentSet: []
      },
      regRefundDateVisible: false,
      regRefundDateData: {
        refundDate: null
      },
      targetRow: null
    }
  },
  computed: {
    define: () => define,
    ...mapGetters(
      'OrderDetail', {
        orderDetail: 'orderDetail',
        siteCode: 'siteCode'
      }),
    validateScope () {
      return 'OrderDetailItemsToolTip'
    },
    hasError () {
      return this.errors.any(this.validateScope)
    },
    rejectedPaymentSets () {
      // 支払処理結果一覧から除外
      const rows = _.reject(this.paymentSets, paymentSet => {
        // 処理コード: ダミー
        return paymentSet.processCode === define.PAYMENT_SET_PROCESS.DUMMY ||
        // 支払処理.ステータス: VOID
        paymentSet.statusCode === define.PAYMENT_SET_STATUS.VOID ||
        // 処理コード: (決済確認 || 返金確認) & 処理済み
          (_.includes([define.PAYMENT_SET_PROCESS.CAPTURE_CHEKKNG, define.PAYMENT_SET_PROCESS.REFUND_CHEKING], paymentSet.processCode) &&
              paymentSet.statusCode === define.PAYMENT_SET_STATUS.PROCESSED) ||
        // 支払処理区分: 支払処理不要
          paymentSet.orderPayment.paymentMethod.methodType === define.PAYMENT_METHOD_TYPE.NO_PROCESS ||
        // 支払処理区分: (サイト決済 || サイト決済,返金連携なし,自動完了しない) & 処理コード: 決済
          (_.includes([define.PAYMENT_METHOD_TYPE.SITE, define.PAYMENT_METHOD_TYPE.SITE_NO_REFUND_NO_COMP], paymentSet.orderPayment.paymentMethod.methodType) &&
              paymentSet.processCode === define.PAYMENT_SET_PROCESS.CAPTURE)
      })
      return rows
    },
    // 支払処理結果一覧
    paymentSetList () {
      return [
        { label: '登録日時', path: 'createdAt', type: 'Date', width: 300,
          funcId: define.PermissionKeys.Order.Detail.PaymentSet.CREATED_AT },
        { label: '支払方法', path: 'orderPayment.paymentMethod.name', type: 'String', width: 300,
          funcId: define.PermissionKeys.Order.Detail.PaymentSet.PAYMENT_METHOD },
        { label: '処理タイプ', path: 'processCode', type: 'String', codeName: 'd_payment_set.process_code', width: 300,
          funcId: define.PermissionKeys.Order.Detail.PaymentSet.PROCESS_TYPE },
        { label: 'ステータス', path: 'statusCode', type: 'String', codeName: 'd_payment_set.status_code', columnKey: 'statusCode', width: 300,
          funcId: define.PermissionKeys.Order.Detail.PaymentSet.STATUS },
        { label: '処理日時', path: 'processedAt', type: 'Date', width: 300,
          funcId: define.PermissionKeys.Order.Detail.PaymentSet.PROCESSED_AT },
        { label: '', path: 'operation', type: 'Slot', class: 'right', columnKey: 'operation',
          isShow: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.CANCEL) ||
            this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.RETRY) ||
            this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.ERROR_IGNORE) ||
            this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.CHANGE_PAYMENT) }
      ]
    },
    showTooltip () {
      return this.mode === define.ORDER_MODE.NORMAL
    },
    editable () {
      return EDITABLE[this.mode] || {}
    },
    regRefundDateEditable () {
      return { refundDate: true }
    },
    // 返金日登録
    regRefundDateTableSetList () {
      return [
        { label: '返金日',
          properties: [{ path: 'refundDate', type: 'Date', elementType: ELEMENT_TYPE.DATE,
            editableId: 'refundDate', validateRule: 'required', validateAs: '返金日' }]
        }
      ]
    },
    regRefundDateValidateScope () {
      return 'regRefundDate'
    },
    regRefundDateHasError () {
      return this.errors.any(this.regRefundDateValidateScope)
    },
    regRefundDateDisableOK () {
      return this.regRefundDateHasError || this.regRefundDateData.refundDate == null
    }
  },
  watch: {
    detailDialogVisible (val) {
      if (!val) {
        this.detailData = {}
      }
    }
  },
  created () {
    this.$validator = this.parentValidator
  },
  methods: {
    /**
     * Tooltipの項目を返却する.
     * @param {Object} row
     * @return {Array}
     */
    getTooltipList (row) {
      return [
        {
          title: 'キャンセル',
          disabled: () => row.statusCode !== define.PAYMENT_SET_STATUS.UNPROCESSED || row.isCancellable !== 1 ||
            this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.CANCEL),
          click: (item) => this.cancel(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.CANCEL)
        },
        {
          title: '再実行',
          disabled: () => row.statusCode !== define.PAYMENT_SET_STATUS.ERROR_END ||
            this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.RETRY),
          click: (item) => this.retry(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.RETRY)
        },
        {
          title: '手動完了',
          disabled: () => row.statusCode !== define.PAYMENT_SET_STATUS.ERROR_END ||
            this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.MANUAL_COMPLETION),
          click: (item) => this.manualCompletion(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.MANUAL_COMPLETION)
        },
        {
          title: '支払方法変更',
          disabled: () => row.statusCode !== define.PAYMENT_SET_STATUS.ERROR_END ||
            !_.includes([define.PAYMENT_SET_PROCESS.CAPTURE, define.PAYMENT_SET_PROCESS.REFUND], row.processCode) ||
            row.amount === 0 || this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.CHANGE_PAYMENT),
          click: (item) => this.changePayment(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.CHANGE_PAYMENT)
        },
        {
          title: 'エラー解除',
          disabled: () => row.statusCode !== define.PAYMENT_SET_STATUS.ERROR_END ||
            this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.ERROR_IGNORE),
          click: (item) => this.errorIgnore(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.ERROR_IGNORE)
        },
        {
          title: '返金日登録',
          // 注文詳細画面モード=0 (通常) and ステータス=0 (未処理) and 処理コード = 4 (返金) and 支払処理区分 = N or N (自動連携しない)
          disabled: () => {
            return row.statusCode !== define.PAYMENT_SET_STATUS.UNPROCESSED || row.processCode !== define.PAYMENT_SET_PROCESS.REFUND || row.isReserved === 1 ||
            ![define.PAYMENT_METHOD_TYPE.CCS_NO_REFUND_NO_COMP, define.PAYMENT_METHOD_TYPE.SITE_NO_REFUND_NO_COMP].includes(_.get(row, 'orderPayment.paymentMethod.methodType', '')) ||
            this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.PaymentSet.REGISTER_REFUND_DATE)
          },
          click: (item) => this.registerRefundDate(row),
          show: this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.REGISTER_REFUND_DATE)
        }
      ]
    },
    async callApi (url, data) {
      try {
        this.$loadingService.show()
        await common.apiAxios(url, data)
        this.$emit('requestUpdate') // 再ロード
      } catch (error) {
        const reason = _.get(ERROR_REASON, error.response.data.reason)
        reason ? common.addError(error, { msg: message.MSG_519({ reason }) }) : common.addError(error, { msg: message.MSG_902() })
        this.$notify.error({ error: error })
        throw error
      } finally {
        this.$loadingService.close()
      }
    },
    // キャンセル
    cancel (row) {
      this.$confirm('予約されている決済処理をキャンセルしますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(() => {
        this.callApi(
          `${config.PAYMENT_URL}/${row.uuid}`,
          { method: 'delete' }
        )
      })
    },
    // 再実行
    retry (row) {
      const orderPayment = _.find(this.orderDetail.payments, { auid: row.orderPayment.auid })
      const msg = orderPayment.isProcessed !== 0 ? `支払処理を再実行しますか？`
        : `この支払処理は支払処理対象外に更新されています。
        再実行しても決済システムに連携せず、正常終了と見なします。
        支払処理を再実行しますか？`
      this.$confirm(msg, define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(() => {
        this.callApi(
          `${config.PAYMENT_URL}/${row.uuid}/errorRetry`,
          { method: 'put' }
        )
      })
    },
    // 手動完了
    manualCompletion (row) {
      this.$confirm('手動完了しますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(() => {
        this.callApi(
          `${config.PAYMENT_URL}/${row.uuid}/manualCompletion`,
          { method: 'put' }
        )
      })
    },
    // 支払方法変更
    async changePayment (row) {
      const existReservedPaymentSet = _.find(this.paymentSets, paymentSet => {
        return paymentSet.orderPayment.auid === row.orderPayment.auid && paymentSet.orderPayment.id > row.orderPayment.id
      })

      if (existReservedPaymentSet) {
        this.$notify.info(`予約中の支払処理が存在します。
        既に金額が変更されている可能性があるため、金額を確認の上、保存してください。`)
      }

      // 変更元支払処理更新用のデータをセット
      this.changePayments.srcPaymentSet = {
        paymentSetUuid: row.uuid,
        uuid: row.orderPayment.uuid,
        paymentMethod: row.orderPayment.paymentMethod.name,
        amount: row.orderPayment.amount,
        isPaid: row.orderPayment.isPaid
      }
      const paymentAmount = row.amount  // 支払処理金額の取得

      this.$store.dispatch({
        type: 'set',
        states: {
          'Order/mode': define.ORDER_MODE.CHANGE_PAYMENT  // 支払方法変更モード
        }
      })

      await this.$emit('initByMode')

      // 変更元の支払処理をセット
      this.orderDetail.paymentSets = [ { uuid: row.uuid } ]

      // 変更元の注文支払の金額を更新
      const srcOrderPayment = _.find(this.orderDetail.payments, { auid: row.orderPayment.auid })
      srcOrderPayment.amount = +srcOrderPayment.amount - +paymentAmount

      // 変更先の注文支払の金額をセット(保留レコード)
      const holdOrderPayment = _.find(this.orderDetail.payments, { paymentMethod: { methodType: define.PAYMENT_METHOD_TYPE.HOLD } })
      if (holdOrderPayment) {
        holdOrderPayment.amount = +holdOrderPayment.amount + +paymentAmount
      } else {
        this.orderDetail.payments.push({
          paymentMethod: {
            code: define.PAYMENT_METHOD_TYPE.HOLD
          },
          amount: paymentAmount,
          isPaid: 1,
          isProcessed: 1,
          needsHoldOption: true
        })
      }

      // 支払内訳が表示されるようスクロール
      const paymentType = document.querySelector('#paymentType')
      if (paymentType) {
        window.scroll({ top: paymentType.offsetTop, behavior: 'smooth' })
      }
    },
    // エラー解除
    errorIgnore (row) {
      this.$confirm('エラー解除しますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(() => {
        this.callApi(
          `${config.PAYMENT_URL}/${row.uuid}/errorIgnore`,
          { method: 'put' }
        )
      })
    },
    // 返金日登録
    async registerRefundDate (row) {
      this.regRefundDateVisible = true
      this.regRefundDateData = { refundDate: null }
      this.targetRow = row
      this.$validator.reset(`${this.regRefundDateValidateScope}.*`)
    },
    async onRegRefundDateOk () {
      await this.$validator.validate(`${this.regRefundDateValidateScope}.*`)
      if (!this.regRefundDateHasError) {
        this.$confirm('返金日登録しますか？', define.MESSAGE_TITLE, {
          confirmButtonText: 'はい',
          cancelButtonText: 'いいえ',
          type: 'warning'
        }).then(async () => {
          this.regRefundDateVisible = false
          await this.callApi(
            `${config.PAYMENT_URL}/${this.targetRow.uuid}/regRefundDate`,
            { method: 'put', data: this.regRefundDateData }
          )
        }).catch(() => {})
      }
    },
    editableCondition (row) {
      return row.uuid == null
    },
    cellClassName ({ row, column }) {
      if (column.columnKey !== 'statusCode') {
        return ''
      }

      return row.statusCode === 9 ? 'payment-set-status-error' : ''
    },
    onRowClick (row, column, event) {
      if (!this.$isFunctionShown(define.PermissionKeys.Order.Detail.PaymentSet.DETAIL)) {
        // 権限がない場合、何もしない
        return
      }
      if (column.columnKey === 'operation') {
        // tooltipのセルクリック時は何もしない
        return
      }
      this.detailData = row
      this.detailDialogVisible = true
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/order/OrderDetailPaymentSet.vue